import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  Character,
  CharacterFeatureBase,
  CharacterFeatureSource,
} from "@dndModels";

import ToggleButtonBox from "@components/input/buttons/ToggleButtonBox";
import DisplayStringWithReplacements from "@dndComponents/display/DisplayStringWithReplacements";
import CharacterFeatureInput from "@dndComponents/character/features/CharacterFeatureInput";
import CharacterFeatureSourcesInput from "@dndComponents/character/features/CharacterFeatureSourcesInput";
import ArrowBackButton from "@components/input/buttons/icon_buttons/ArrowBackButton";

interface Props {
  char: Character;
  sources: CharacterFeatureSource[];
  log_it?: boolean;
  onCFSSelect?: (selected: CharacterFeatureSource | undefined) => void;
  extra_cfs_header_control?: (cfs: CharacterFeatureSource) => React.ReactNode;
  extra_cfs_controls?: (cfs: CharacterFeatureSource) => React.ReactNode[];
  extra_bottom_controls?: () => React.ReactNode[];
  cfs_sort?: (a: CharacterFeatureSource, b: CharacterFeatureSource) => number;
  change_time: number;
  expanded_cfs_id: string;
  show_cfb_levels?: boolean;
}

export default function CharacterFeatureSourcesOuter({
  char,
  sources,
  log_it = false,
  onCFSSelect,
  extra_cfs_header_control,
  extra_cfs_controls,
  extra_bottom_controls,
  cfs_sort,
  change_time,
  expanded_cfs_id,
  show_cfb_levels = false
}: Props) {
  const [selected_cfb, setSelectedCFB] = useState<CharacterFeatureBase | undefined>(undefined);
  const [show_desc, setShowDesc] = useState(false);
  const [prev_change_time, setChangeTime] = useState(0);
  const [reloading, setReloading] = useState(false);

  useEffect(() => {
    if (change_time !== prev_change_time) {
      setChangeTime(change_time);
      setReloading(true);
    }
  }, [change_time, prev_change_time, setChangeTime]);

  useEffect(() => {
    if (reloading) {
      console.log("reloading");
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const source = expanded_cfs_id !== "" ? sources.find(o => o.true_id === expanded_cfs_id) : (sources.length === 1 ? sources[0] : undefined);

  if (window.innerWidth > 800) {
    // const cfbs = source ? getCharFeatureBasesForRender(source.char_feature_bases) : [];
    // console.log(sources);
    // console.log(source);
    // console.log(expanded_cfs_id);
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          size={{
            xs: 4
          }}>
          <div style={{ maxHeight: window.innerHeight - 99, overflowY: "auto" }}>
            <CharacterFeatureSourcesInput
              char={char}
              sources={sources}
              log_it={log_it}
              onCFSSelect={(cfs: CharacterFeatureSource | undefined) => {
                if (onCFSSelect) {
                  onCFSSelect(cfs);
                }
                setSelectedCFB(undefined);
                setReloading(true);
              }}
              onCFBSelect={(changed: CharacterFeatureBase | undefined) => {
                setSelectedCFB(changed);
                setReloading(true);
              }}
              extra_cfs_header_control={extra_cfs_header_control}
              extra_cfs_controls={extra_cfs_controls}
              extra_bottom_controls={extra_bottom_controls}
              cfs_sort={cfs_sort}
              change_time={change_time}
              expanded_cfs_id={sources.length === 1 ? sources[0].true_id : expanded_cfs_id}
              expanded_cfb_id={selected_cfb?.true_id??""}
              show_cfb_levels={show_cfb_levels}
            />
          </div>
        </Grid>
        <Grid
          size={{
            xs: 8
          }}>
          <div style={{ maxHeight: window.innerHeight - 99, overflowY: "auto" }}>
            { selected_cfb && source ? 
              <Grid container spacing={0} direction="row">
                { selected_cfb.feature_base && 
                  selected_cfb.feature_base.description !== "" &&
                  (selected_cfb.features.length !== 1 || 
                    !(selected_cfb.features[0].floating_feature && 
                      selected_cfb.features[0].floating_feature.description === selected_cfb.feature_base.description)) &&
                  <Grid
                    size={{
                      xs: 12
                    }}>
                    { (selected_cfb.feature_base.description.length < 30 || selected_cfb.features.length === 0) ?
                      <DisplayStringWithReplacements
                        display_string={selected_cfb.feature_base.description}
                      />
                    :
                      <Grid container spacing={0} direction="column">
                        <Grid>
                          <ToggleButtonBox 
                            name={ show_desc ? "Hide Description" : "Show Description" }
                            value={show_desc}
                            onToggle={() => {
                              setShowDesc(!show_desc);
                            }}
                          />
                        </Grid>
                        { show_desc &&
                          <Grid>
                            <DisplayStringWithReplacements display_string={ selected_cfb.feature_base.description } />
                          </Grid>
                        }
                      </Grid>
                    }
                  </Grid>
                }
                { selected_cfb.features.map((feature, key) => {
                  if (feature.feature) {
                    return (
                      <Grid
                        key={key}
                        size={{
                          xs: 12
                        }}>
                        <CharacterFeatureInput 
                          obj={feature}
                          character={char}
                          source={source}
                        />
                      </Grid>
                    );
                  }
                  return null;
                })}
              </Grid>
            : source &&
              <Grid container spacing={0} direction="row">
                <Grid
                  size={{
                    xs: 12
                  }}>
                  <DisplayStringWithReplacements
                    display_string={source.description}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 12
                  }}>
                  <DisplayStringWithReplacements
                    display_string={source.notes}
                  />
                </Grid>
              </Grid>
            }
          </div>
        </Grid>
      </Grid>
    );
  } else {
    if (selected_cfb && source) {
      return (
        <Grid container spacing={0} direction="row">
          <Grid size={12}>
            <ArrowBackButton
              fab
              onClick={() => { setSelectedCFB(undefined); }}
            />
          </Grid>
          { selected_cfb.feature_base && 
            selected_cfb.feature_base.description !== "" &&
            (selected_cfb.features.length !== 1 || 
              !(selected_cfb.features[0].floating_feature && 
                selected_cfb.features[0].floating_feature.description === selected_cfb.feature_base.description)) &&
            <Grid size={12}>
              { (selected_cfb.feature_base.description.length < 30 || selected_cfb.features.length === 0) ?
                <DisplayStringWithReplacements
                  display_string={selected_cfb.feature_base.description}
                />
              :
                <Grid container spacing={0} direction="column">
                  <Grid>
                    <ToggleButtonBox 
                      name={ show_desc ? "Hide Description" : "Show Description" }
                      value={show_desc}
                      onToggle={() => {
                        setShowDesc(!show_desc);
                      }}
                    />
                  </Grid>
                  { show_desc &&
                    <Grid>
                      <DisplayStringWithReplacements display_string={ selected_cfb.feature_base.description } />
                    </Grid>
                  }
                </Grid>
              }
            </Grid>
          }
          { selected_cfb.features.map((feature, key) => {
            if (feature.feature) {
              return (
                <Grid
                  key={key}
                  size={{
                    xs: 12
                  }}>
                  <CharacterFeatureInput 
                    obj={feature}
                    character={char}
                    source={source}
                  />
                </Grid>
              );
            }
            return null;
          })}
        </Grid>
      );
    } else {
      return (
        <CharacterFeatureSourcesInput
          char={char}
          sources={sources}
          log_it={log_it}
          // onChange={onChange}
          change_time={change_time}
          expanded_cfs_id={expanded_cfs_id}
          show_cfb_levels={show_cfb_levels}
          extra_cfs_header_control={extra_cfs_header_control}
          extra_cfs_controls={extra_cfs_controls}
          extra_bottom_controls={extra_bottom_controls}
          cfs_sort={cfs_sort}
          onCFSSelect={(cfs: CharacterFeatureSource | undefined) => {
            if (onCFSSelect) {
              onCFSSelect(cfs);
            }
          }}
          onCFBSelect={(changed: CharacterFeatureBase | undefined) => {
            setSelectedCFB(changed);
            setReloading(true);
          }}
          expanded_cfb_id={selected_cfb?.true_id??""}
        />
      );
    }
  }
}
